import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faMapLocationDot, faUser } from '@fortawesome/free-solid-svg-icons';
import SidebarPageLayout from '../../components/layout/SidebarPageLayout';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { useAuth } from '../../components/auth/AuthProvider';
import { useSessionData } from '../../components/data/SessionData';
import { canViewOrganizationSettings } from '../../auth/appRoles';
import { fetchUserSettings, updateUserSetting } from '../../api/auth';
import { normalizeTemperatureUnit } from '../../util/temperature';

function siteKey(site) {
  if (site?.siteId != null && site.siteId !== '') {
    return String(site.siteId);
  }
  return String(site?.slug || site?.name || '').trim();
}

function titleLabel(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatSitePlace(location) {
  const raw = String(location ?? '').trim();
  if (!raw) {
    return '';
  }
  const parts = (raw.includes(',') ? raw.split(',') : raw.split(/\s+/))
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 3) {
    return raw;
  }
  const country = parts[0];
  const state = parts[1];
  const city = parts.slice(2).join(' ');
  return `${city}, ${state} ${country}`;
}

function formatAxis(value, positive, negative) {
  const hemisphere = value < 0 ? negative : positive;
  return `${Math.abs(value)}° ${hemisphere}`;
}

function coordinatePair(latitude, longitude) {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }
  return { lat, lon };
}

function formatCoordinates(latitude, longitude) {
  const pair = coordinatePair(latitude, longitude);
  if (!pair) {
    return '';
  }
  return `${formatAxis(pair.lat, 'N', 'S')}, ${formatAxis(pair.lon, 'E', 'W')}`;
}

function siteMapSrc(latitude, longitude) {
  const pair = coordinatePair(latitude, longitude);
  if (!pair) {
    return '';
  }
  const query = new URLSearchParams({
    q: `${pair.lat},${pair.lon}`,
    z: '8',
    hl: 'en',
    output: 'embed',
  });
  return `https://maps.google.com/maps?${query.toString()}`;
}

function siteMapLink(latitude, longitude) {
  const pair = coordinatePair(latitude, longitude);
  if (!pair) {
    return '';
  }
  const query = new URLSearchParams({
    api: '1',
    query: `${pair.lat},${pair.lon}`,
  });
  return `https://www.google.com/maps/search/?${query.toString()}`;
}

function formatCreatedAt(value) {
  if (value == null || value === '') {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function Settings() {
  const { hash } = useLocation();
  const { user, patchUser } = useAuth();
  const [temperatureUnit, setTemperatureUnit] = useState(() => normalizeTemperatureUnit(user?.temperature_unit));
  const [savingTemperature, setSavingTemperature] = useState(false);
  const [temperatureError, setTemperatureError] = useState('');
  const { sites, isSessionDataLoading } = useSessionData();
  const showOrganization = canViewOrganizationSettings(user?.role_id);
  const siteRecords = useMemo(
    () => (Array.isArray(sites) ? sites.filter((site) => String(site?.name ?? '').trim()) : []),
    [sites],
  );
  const [selectedSiteKey, setSelectedSiteKey] = useState('');
  const organization = String(user?.organization ?? '').trim();
  const organizationUrl = /^https?:\/\//i.test(String(user?.organization_url ?? '').trim())
    ? String(user.organization_url).trim()
    : '';

  const selectedSite = siteRecords.find((site) => siteKey(site) === selectedSiteKey) || null;
  const selectedSiteName = String(selectedSite?.name ?? '').trim();
  const siteType = selectedSite?.siteType;
  const sitePlace = formatSitePlace(selectedSite?.location);
  const siteCoordinates = formatCoordinates(selectedSite?.latitude, selectedSite?.longitude);
  const siteMap = siteMapSrc(selectedSite?.latitude, selectedSite?.longitude);
  const siteMapHref = siteMapLink(selectedSite?.latitude, selectedSite?.longitude);
  const siteCreated = formatCreatedAt(selectedSite?.createdAt);

  useEffect(() => {
    document.title = 'Vivosol Plant Performance Insights Hub | Admin / Settings';
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchUserSettings();
        const row = (Array.isArray(data?.settings) ? data.settings : [])
          .find((item) => item?.setting_slug === 'temperature');
        const unit = normalizeTemperatureUnit(row?.setting_value);
        if (!cancelled) {
          setTemperatureUnit(unit);
          patchUser({ temperature_unit: unit });
        }
      } catch (error) {
        console.warn('Failed to load user settings:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patchUser]);

  async function handleTemperatureChange(event) {
    const next = normalizeTemperatureUnit(event.target.value);
    const previous = temperatureUnit;
    setTemperatureUnit(next);
    setTemperatureError('');
    setSavingTemperature(true);
    patchUser({ temperature_unit: next });

    try {
      await updateUserSetting({ settingSlug: 'temperature', settingValue: next });
    } catch (error) {
      console.warn('Failed to save temperature preference:', error);
      setTemperatureUnit(previous);
      patchUser({ temperature_unit: previous });
      setTemperatureError('Unable to update temperature preference.');
    } finally {
      setSavingTemperature(false);
    }
  }

  useEffect(() => {
    if (!siteRecords.length) {
      setSelectedSiteKey('');
      return;
    }
    setSelectedSiteKey((current) => (
      siteRecords.some((site) => siteKey(site) === current) ? current : siteKey(siteRecords[0])
    ));
  }, [siteRecords]);

  useEffect(() => {
    const id = String(hash || '').replace(/^#/, '');
    if (!id || (id === 'organization' && !showOrganization)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [hash, showOrganization]);

  return (
    <SidebarPageLayout id="adminSettings">
      <div className="content p-5">
        <h1 className="mb-0">Settings</h1>
        <Breadcrumbs />
        <div className="settings-panels">
          <div id="preferences" className="panel p-3">
            <div className="header d-flex justify-content-between pb-3">
              <div className="left d-flex gap-3">
                <span><FontAwesomeIcon icon={faUser} /> USER</span>
              </div>
            </div>
            <div className="body pt-3">
              <div className="settings-preference">
                <label htmlFor="temperature-unit">Temperature</label>
                <select
                  id="temperature-unit"
                  className="form-select settings-preference-select"
                  value={temperatureUnit}
                  disabled={savingTemperature}
                  onChange={handleTemperatureChange}
                >
                  <option value="fahrenheit">Fahrenheit</option>
                  <option value="celsius">Celsius</option>
                </select>
                {savingTemperature ? (
                  <span className="settings-preference-spinner" role="status" aria-label="Updating temperature" />
                ) : null}
              </div>
              {temperatureError ? <p className="settings-preference-error">{temperatureError}</p> : null}
            </div>
          </div>
          <div id="site-preferences" className="panel p-3">
            <div className="header d-flex justify-content-between pb-3">
              <div className="left d-flex gap-3 align-items-center">
                <span>
                  <FontAwesomeIcon icon={faMapLocationDot} /> SITE
                  {selectedSiteName ? ' : ' : ''}
                </span>
                {selectedSiteName ? (
                  siteRecords.length > 1 ? (
                    <select
                      className="settings-site-select"
                      aria-label="Site"
                      value={selectedSiteKey}
                      onChange={(event) => setSelectedSiteKey(event.target.value)}
                    >
                      {siteRecords.map((site) => {
                        const key = siteKey(site);
                        return (
                          <option key={key} value={key}>{String(site.name).trim()}</option>
                        );
                      })}
                    </select>
                  ) : (
                    <span className="settings-site-name">{selectedSiteName}</span>
                  )
                ) : null}
              </div>
            </div>
            <div className="body pt-3">
              {selectedSite ? (
                <div className="settings-site-layout" key={selectedSiteKey}>
                <div className="settings-site-facts">
                  <div className="settings-site-line">
                    <span className="settings-site-label">Type</span>
                    <span className="settings-site-value">
                      {siteType?.slug ? (
                        <Link
                          className="settings-site-type-link"
                          to={`/help/documentation/site-types#${siteType.slug}`}
                        >
                          {titleLabel(siteType.typeName || siteType.slug)}
                        </Link>
                      ) : '—'}
                    </span>
                  </div>
                  <div className="settings-site-line">
                    <span className="settings-site-label">Location</span>
                    <span className="settings-site-value">
                      {sitePlace || '—'}
                      {siteCoordinates ? (
                        <a
                          className="settings-site-coords"
                          href={siteMapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ({siteCoordinates})
                        </a>
                      ) : null}
                    </span>
                  </div>
                  <div className="settings-site-line">
                    <span className="settings-site-label">Timezone</span>
                    <span className="settings-site-value">{String(selectedSite.timezone ?? '').trim() || '—'}</span>
                  </div>
                  <div className="settings-site-line">
                    <span className="settings-site-label">Created At</span>
                    <span className="settings-site-value">{siteCreated || '—'}</span>
                  </div>
                </div>
                {siteMap ? (
                  <a
                    className="settings-site-map"
                    href={siteMapHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${selectedSiteName || 'this site'} in Google Maps`}
                  >
                    <iframe
                      title={`${selectedSiteName || 'Site'} location`}
                      src={siteMap}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      tabIndex={-1}
                    />
                  </a>
                ) : null}
                </div>
              ) : (
                <p>{isSessionDataLoading ? 'Loading sites…' : 'No sites are available.'}</p>
              )}
            </div>
          </div>
          {showOrganization ? (
            <div id="organization" className="panel p-3">
              <div className="header d-flex justify-content-between pb-3">
                <div className="left d-flex gap-3 align-items-center">
                  <span>
                    <FontAwesomeIcon icon={faBuilding} /> ORGANIZATION
                    {organization ? ' : ' : ''}
                  </span>
                  {organization ? (
                    organizationUrl ? (
                      <a
                        className="settings-org-link"
                        href={organizationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {organization}
                      </a>
                    ) : (
                      <span>{organization}</span>
                    )
                  ) : null}
                </div>
              </div>
              <div className="body pt-3">
                <p>Organization settings will be implemented soon.</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Settings;
